import axios, { AxiosError } from 'axios';
import { ApiErrorDataDtoSchema } from './api.contracts';
import { normalizeValidationErrors } from './api.lib';
import { env } from '~shared/config/env';
import { oneIdentifier, isStaging, isLocal } from '~shared/config/hostname';

// Worktree-aware: the dev server inherits VITE_BACKEND_URL from the
// direnv-loaded environment (started via bin/stack/stack → bin/stack/frontend-start), so
// each parallel worktree's frontend talks to its own backend on the offset
// port. Falls back to the default local backend outside the managed dev env.
const LOCAL_API_URL = `${
  import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:18080'
}/rest/v1/samera-one`;

export const api = axios.create({
  baseURL: `${
    isLocal
      ? LOCAL_API_URL
      : isStaging
      ? env.STAGING_API_URL ?? env.API_URL
      : env.API_URL
  }/${oneIdentifier}`,
});

// Kept module-level so the stale-session interceptor below can compare the id a
// response was issued for against the one currently in use.
let readSessionId: () => string | undefined = () => undefined;

export const attachAuthInterceptor = (
  getSessionId: () => string | undefined,
) => {
  readSessionId = getSessionId;
  api.interceptors.request.use(
    config => {
      const sessionId = getSessionId();
      if (sessionId) {
        config.headers['x-samera-session-id'] = sessionId;
      }
      return config;
    },
    error => Promise.reject(error),
  );
};

// Detect a stale persisted sessionId: when a request that carries the
// x-samera-session-id header gets back the backend's `no_session` error
// (NoSessionException → HTTP 400), the session is gone server-side. Drop it
// so the next request gets a fresh one. Wired up in app.tsx; matched on the
// error code in the response body, not the HTTP status, because a 404 match
// would also catch /session/last-booking which legitimately returns 404 when
// there is no booking yet — that previous matching caused a reset cascade
// on the payment success page.
let onStaleSessionDetected: (() => void) | undefined;

export const attachSessionResetInterceptor = (callback: () => void) => {
  onStaleSessionDetected = callback;
};

api.interceptors.response.use(
  response => response,
  error => {
    // The id must still be the CURRENT one: responses can land out of order, so
    // a slow reply for an already-replaced session would otherwise wipe the
    // fresh one the guest is now using.
    const sentSessionId = error?.config?.headers?.['x-samera-session-id'];
    if (
      axios.isAxiosError(error) &&
      onStaleSessionDetected &&
      sentSessionId &&
      sentSessionId === readSessionId() &&
      typeof error.response?.data === 'object' &&
      error.response.data !== null &&
      Array.isArray((error.response.data as { errors?: unknown }).errors) &&
      (
        error.response.data as { errors: Array<{ error?: string }> }
      ).errors.some(e => e?.error === 'no_session')
    ) {
      onStaleSessionDetected();
    }

    if (!axios.isAxiosError(error)) {
      // Ensure we always reject with an Error object
      if (error instanceof Error) {
        return Promise.reject(error);
      }
      // Convert non-Error rejections to Error objects
      const errorMessage =
        typeof error === 'string'
          ? error
          : typeof error === 'object' && error !== null
          ? JSON.stringify(error)
          : 'Unknown error';
      return Promise.reject(new Error(errorMessage));
    }

    const validation = ApiErrorDataDtoSchema.safeParse(error.response?.data);

    if (!validation.success) {
      // If the error response doesn't match our schema, ensure we still return a proper Error
      // Extract error message from response data if it's a string or object
      let errorMessage = error.message;
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (
          typeof error.response.data === 'object' &&
          error.response.data !== null
        ) {
          // Try to extract a meaningful message
          const data = error.response.data as Record<string, unknown>;
          errorMessage =
            (data.message as string) ||
            (data.error as string) ||
            JSON.stringify(data) ||
            errorMessage;
        }
      }

      const enhancedError = new AxiosError(
        errorMessage,
        error.code,
        error.config,
        error.request,
        error.response,
      );
      return Promise.reject(enhancedError);
    }

    const normalizedErrorResponse = {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...error.response!,
      data: normalizeValidationErrors(validation.data),
    };

    const normalizedError = new AxiosError(
      error.message,
      error.code,
      error.config,
      error.request,
      normalizedErrorResponse,
    );

    return Promise.reject(normalizedError);
  },
);
